const ANON_ID_COOKIE = 'ajs_anonymous_id'

/**
 * Read Segment's `ajs_anonymous_id` from the browser cookie so we can seed
 * Jitsu with the same value. Without this, every logged-out user gets a fresh
 * anonymousId post-cutover and BigQuery funnel joins break across the
 * boundary. Returns `null` if the cookie is missing or unparseable.
 */
export function readSegmentAnonymousId() {
    try {
        const match = document.cookie.match(
            new RegExp('(?:^|; )' + ANON_ID_COOKIE + '=([^;]*)')
        )
        if (!match) {
            return null
        }
        // Segment stores the id either bare or wrapped in quotes.
        const raw = decodeURIComponent(match[1])
        return raw.replace(/^"|"$/g, '') || null
    } catch (_e) {
        return null
    }
}

/**
 * Stop @jitsu/js from auto-attaching identified-user traits to every event.
 *
 * By default, @jitsu/js persists the second arg of identify(userId, traits)
 * under the `__user_traits` storage key and re-reads it on every track/page
 * to set `context.traits` (jitsu.cjs.js: restoreTraits + adjustPayload). That
 * means a logged-in user's name / accountId / email rides along with every
 * page view, bloating payloads and duplicating PII we already send on the
 * identify itself.
 *
 * There's no narrowly-scoped opt-out (`privacy.disableUserIds: true` also
 * nukes anonymousId, which we need for cross-event stitching). So we drop
 * the writes at the storage layer: identify events still go out with full
 * traits (the payload is built from the dispatched action, not from storage),
 * but `restoreTraits` returns `{}` for every subsequent track/page.
 *
 * Intercepting setItem instead of clearing after each identify avoids the
 * race where a track/page fires between identify() and the post-identify
 * clear.
 * @param {object} jitsu @jitsu/js analytics instance
 */
export function suppressJitsuTraitAutoAttach(jitsu) {
    try {
        const origSetItem = jitsu.storage.setItem.bind(jitsu.storage)
        jitsu.storage.setItem = (key, value, options) => {
            if (key === '__user_traits') {
                return
            }
            return origSetItem(key, value, options)
        }
    } catch (_e) {
        // If the storage surface ever changes shape, fail open rather than
        // breaking the page.
    }
}

/**
 * Install a fan-out shim on `window.analytics.{track,identify,page,reset}`
 * that forwards every call to Jitsu in addition to Segment. Existing call
 * sites keep working unchanged. The shim respects per-side flags so either
 * leg can be flipped off independently during cutover.
 * @param {object} jitsu @jitsu/js analytics instance
 * @param {boolean} segmentEnabled whether to keep firing into Segment
 */
export function installFanOutShim(jitsu, segmentEnabled) {
    if (!window.analytics || typeof window.analytics.track !== 'function') {
        return
    }

    const origTrack = window.analytics.track.bind(window.analytics)
    const origIdentify = window.analytics.identify.bind(window.analytics)
    const origPage = window.analytics.page.bind(window.analytics)
    const origReset =
        typeof window.analytics.reset === 'function'
            ? window.analytics.reset.bind(window.analytics)
            : null

    window.analytics.track = function (event, properties, options, callback) {
        if (segmentEnabled) {
            origTrack(event, properties, options, callback)
        }
        try {
            jitsu.track(event, properties || {})
        } catch (_e) {
            // Swallow — Jitsu must never break the page.
        }
    }

    window.analytics.identify = function (userId, traits, options, callback) {
        if (segmentEnabled) {
            origIdentify(userId, traits, options, callback)
        }
        try {
            jitsu.identify(userId, traits || {})
        } catch (_e) {
            // Swallow.
        }
    }

    window.analytics.page = function (...args) {
        if (segmentEnabled) {
            origPage(...args)
        }
        try {
            jitsu.page(...args)
        } catch (_e) {
            // Swallow.
        }
    }

    // Without this, Jitsu retains the previous user's identity after logout
    // and leaks it into anonymous browsing and the next user's session.
    window.analytics.reset = function (...args) {
        if (segmentEnabled && origReset) {
            origReset(...args)
        }
        try {
            jitsu.reset()
        } catch (_e) {
            // Swallow.
        }
    }
}

/**
 * Create a Jitsu instance, seed it with Segment's anonymousId, suppress trait
 * auto-attach, and install the fan-out shim on `window.analytics` — re-wrapping
 * once Segment's async script replaces the methods. Returns the instance, or
 * `null` if Jitsu is disabled / misconfigured / `window.analytics` is absent.
 *
 * This is the single entry point shared by every surface that loads Segment
 * (Nuxt marketplace SPA, Livewire/Blade signup + payment pages, …) so the
 * dual-write behaviour stays identical everywhere.
 *
 * `jitsuAnalytics` is injected by the caller rather than imported here: each
 * frontend bundle resolves `@jitsu/js` from its own node_modules, and this
 * shared module lives outside every bundle's root where a bare import would
 * not resolve.
 *
 * @param {object}   config
 * @param {Function} config.jitsuAnalytics the `@jitsu/js` factory
 * @param {string}   config.host           Jitsu host
 * @param {string}   config.writeKey       Jitsu browser write key
 * @param {boolean}  config.segmentEnabled whether Segment is still firing
 * @param {boolean}  config.jitsuEnabled   whether to install the Jitsu leg
 * @returns {object|null}
 */
export function installJitsuFanOut({
    jitsuAnalytics,
    host,
    writeKey,
    segmentEnabled,
    jitsuEnabled,
}) {
    if (!jitsuEnabled || !writeKey || typeof jitsuAnalytics !== 'function') {
        return null
    }
    if (!window.analytics) {
        return null
    }

    const jitsu = jitsuAnalytics({ host, writeKey })

    suppressJitsuTraitAutoAttach(jitsu)

    // Preserve anonymousId continuity at cutover so logged-out funnel joins in
    // BigQuery survive the boundary.
    const segmentAnonId = readSegmentAnonymousId()
    if (segmentAnonId) {
        try {
            jitsu.setAnonymousId(segmentAnonId)
        } catch (_e) {
            // Not fatal.
        }
    }

    window.jitsu = jitsu

    // Install immediately so events fire even if Segment's CDN script 404s, is
    // blocked, or is slow to load. Re-install on `analytics.ready` because
    // Segment replaces every method on window.analytics once analytics.min.js
    // loads — the second pass re-wraps the freshly-installed real functions.
    // Both passes are idempotent: each one wraps whatever is currently on
    // window.analytics, so they don't compound.
    installFanOutShim(jitsu, segmentEnabled)
    if (segmentEnabled && typeof window.analytics.ready === 'function') {
        window.analytics.ready(() => installFanOutShim(jitsu, segmentEnabled))
    }

    return jitsu
}
